// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Posegraf tool's pure logic (frontend-r22.7): edge identity in the URL,
 * the strained-edge list, the add-edge form's validation, the ICP verdict and
 * suggested weight, and the Danish copy for the pose-graph routes' errors.
 *
 * DOM-free so vitest covers it; `routes/GraphViewPage.tsx` and
 * `components/PoseGraphEdgeInspector.tsx` are the callers.
 *
 * Contract notes (ruxd `docs/api/openapi.yaml`, slam tag): an edge is named by
 * its pair (either order) plus its type. `DELETE /posegraph/edges/{from}/{to}`
 * does not erase it but marks it **rejected** — `rux optimize` leaves it out
 * and `rux graph discover` does not re-add it — and `POST /posegraph/edges`
 * with the same pair and type brings it back. `GET /posegraph` omits rejected
 * edges and carries no status or source field, so the tool can only list the
 * edges it rejected itself in this session.
 */

import { ApiRequestError } from '../api/client';
import type {
  IcpRefineResult,
  Job,
  PoseGraph,
  PoseGraphEdge,
  PoseGraphEdgeCreate,
  PoseGraphEdgeType,
} from '../api/types';
import { poseTranslation } from '../data/framesModel';
import type { ViewPreset } from '../viewport/cameraViews';
import { EDGE_BASE_COLORS, type ProjectionPlane } from '../viewport/posegraphLayer';

/** The tool's route: Rediger › Scan & pose (frontend-u0o.1; `/graph-view` redirects here). */
export const POSEGRAF_PATH = '/rediger/scan';

export const EDGE_TYPES: readonly PoseGraphEdgeType[] = ['odometry', 'loop_closure', 'panorama'];

export const EDGE_TYPE_LABEL: Record<PoseGraphEdgeType, string> = {
  odometry: 'Odometri',
  loop_closure: 'Loop-lukning',
  panorama: 'Panorama',
};

/** An edge as the tool names it: pair plus type. */
export interface EdgeRef {
  from: number;
  to: number;
  type: PoseGraphEdgeType;
}

/**
 * The CSS colour of an edge type's legend swatch: the same linear-rgb triple
 * the 3D scene draws the type with (`EDGE_BASE_COLORS`), so legend and
 * lines cannot drift. Inside the 3D canvas, which is near-black in both
 * themes, so no theme variant is needed.
 */
export function edgeSwatchCss(type: PoseGraphEdgeType): string {
  const [r, g, b] = EDGE_BASE_COLORS[type];
  return `rgb(${Math.round(r * 255)} ${Math.round(g * 255)} ${Math.round(b * 255)})`;
}

/**
 * The camera that goes with a flattened graph: looking straight at the plane,
 * orthographic, so the 2D graph reads without perspective. 3D = free orbit.
 */
export function planeView(plane: ProjectionPlane): { preset: ViewPreset; projection: 'orthographic' } | null {
  switch (plane) {
    case 'XY':
      return { preset: 'top', projection: 'orthographic' };
    case 'XZ':
      return { preset: 'front', projection: 'orthographic' };
    case 'YZ':
      return { preset: 'right', projection: 'orthographic' };
    default:
      return null;
  }
}

// --------------------------------------------------------------- identity --

/** `?edge=` value: `from-to-type`, e.g. `101-112-loop_closure`. */
export function edgeParam(e: EdgeRef): string {
  return `${e.from}-${e.to}-${e.type}`;
}

/** Parse `?edge=`; null for anything a link from this tool did not write. */
export function parseEdgeParam(raw: string | null | undefined): EdgeRef | null {
  if (!raw) return null;
  const m = /^(\d+)-(\d+)-(odometry|loop_closure|panorama)$/.exec(raw);
  if (!m) return null;
  const from = Number(m[1]);
  const to = Number(m[2]);
  if (!Number.isSafeInteger(from) || !Number.isSafeInteger(to) || from === to) return null;
  return { from, to, type: m[3] as PoseGraphEdgeType };
}

/** Same edge: same type, same pair in either order (the server's rule). */
export function sameEdge(a: EdgeRef, b: EdgeRef): boolean {
  if (a.type !== b.type) return false;
  return (a.from === b.from && a.to === b.to) || (a.from === b.to && a.to === b.from);
}

/** The stored edge a ref names, or null (rejected since, or never there). */
export function findEdge(graph: PoseGraph | null | undefined, ref: EdgeRef | null): PoseGraphEdge | null {
  if (!graph || !ref) return null;
  return graph.edges.find((e) => sameEdge(e, ref)) ?? null;
}

// ------------------------------------------------------------- statistics --

export function edgeCounts(graph: PoseGraph | null | undefined): Record<PoseGraphEdgeType, number> {
  const out: Record<PoseGraphEdgeType, number> = { odometry: 0, loop_closure: 0, panorama: 0 };
  for (const e of graph?.edges ?? []) out[e.type] += 1;
  return out;
}

/**
 * The most strained edges of the visible types, highest residual first: the
 * ones worth looking at when the model has a seam. Edges whose endpoints have
 * no node are left out — the viewport cannot show them either.
 */
export function strainedEdges(
  graph: PoseGraph | null | undefined,
  visible: Record<PoseGraphEdgeType, boolean>,
  limit = 8,
): PoseGraphEdge[] {
  if (!graph) return [];
  const nodes = new Set(graph.nodes.map((n) => n.id));
  return graph.edges
    .filter((e) => visible[e.type] && nodes.has(e.from) && nodes.has(e.to))
    .sort((a, b) => b.residual - a.residual || a.from - b.from || a.to - b.to)
    .slice(0, Math.max(0, limit));
}

/**
 * The residual slider's top end: the largest residual, rounded up to a
 * readable step, so the slider spans the data whatever its scale (the old
 * fixed 0–10 was useless for a graph whose residuals are all below 0.1).
 */
export function residualScale(graph: PoseGraph | null | undefined): number {
  const max = Math.max(0, ...(graph?.edges ?? []).map((e) => (Number.isFinite(e.residual) ? e.residual : 0)));
  if (max <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  return Math.ceil(max / magnitude) * magnitude;
}

/** Edges above the threshold; 0 = threshold off. */
export function countAbove(graph: PoseGraph | null | undefined, threshold: number): number {
  if (!graph || threshold <= 0) return 0;
  return graph.edges.filter((e) => e.residual > threshold).length;
}

const COUNT = new Intl.NumberFormat('da-DK');

/** A residual for display: four significant digits, Danish decimal comma. */
export function formatResidual(x: number): string {
  if (!Number.isFinite(x)) return '–';
  if (x === 0) return '0';
  return x.toLocaleString('da-DK', { maximumSignificantDigits: 4 });
}

/** One line under the tool's heading; null while loading. */
export function graphSummary(graph: PoseGraph | null | undefined, error?: Error | null): string | null {
  if (error) return null;
  if (!graph) return null;
  if (graph.nodes.length === 0) return 'Ingen positioner gemt endnu — kør pipelinen (Punktsky) først.';
  if (graph.edges.length === 0) {
    return `${COUNT.format(graph.nodes.length)} positioner, ingen kanter — kør optimering for at gemme posegrafen.`;
  }
  const c = edgeCounts(graph);
  const parts = [`${COUNT.format(graph.nodes.length)} positioner`, `${COUNT.format(c.odometry)} odometri`];
  if (c.loop_closure > 0) parts.push(`${COUNT.format(c.loop_closure)} loop-lukninger`);
  if (c.panorama > 0) parts.push(`${COUNT.format(c.panorama)} panorama`);
  return parts.join(' · ');
}

// ------------------------------------------------------------- add edge ----

export interface EdgeDraft {
  from: string;
  to: string;
  type: PoseGraphEdgeType;
  weight: string;
}

export type DraftCheck = { ok: true; edge: PoseGraphEdgeCreate } | { ok: false; error: string };

const FRAME_ID = /^\d+$/;

/** Parse a decimal typed with either a comma or a point. */
export function parseDecimal(raw: string): number {
  const t = raw.trim().replace(',', '.');
  if (!/^\d*\.?\d+(e[+-]?\d+)?$/i.test(t)) return NaN;
  return Number(t);
}

/**
 * Validate the add-edge form. `nodes` = the graph's node ids: an edge to a
 * frame without a stored pose would be refused by the optimizer, so it is
 * caught here with a sentence instead of a 400.
 */
export function validateEdgeDraft(draft: EdgeDraft, nodes: ReadonlySet<number> | null): DraftCheck {
  const f = draft.from.trim();
  const t = draft.to.trim();
  if (!FRAME_ID.test(f) || !FRAME_ID.test(t)) return { ok: false, error: 'Fra og til skal være billednumre (hele tal).' };
  const from = Number(f);
  const to = Number(t);
  if (from === to) return { ok: false, error: 'En kant skal forbinde to forskellige billeder.' };
  if (nodes) {
    const missing = [from, to].filter((id) => !nodes.has(id));
    if (missing.length > 0) {
      return { ok: false, error: `Billede ${missing.join(' og ')} har ingen position i posegrafen.` };
    }
  }
  const weight = parseDecimal(draft.weight);
  if (!Number.isFinite(weight) || weight <= 0) return { ok: false, error: 'Vægten skal være et positivt tal.' };
  return { ok: true, edge: { from, to, type: draft.type, weight } };
}

/** The form's starting point; a pair from the image compare seeds from/to. */
export function emptyDraft(from?: number | null, to?: number | null): EdgeDraft {
  return {
    from: from === null || from === undefined ? '' : String(from),
    to: to === null || to === undefined ? '' : String(to),
    type: 'loop_closure',
    weight: '1',
  };
}

// -------------------------------------------------------------------- ICP --

export type IcpTone = 'good' | 'fair' | 'poor';

/**
 * How far to trust an ICP result. Thresholds: within 3 cm RMS with half the
 * points overlapping is a usable constraint; above 10 cm, under 20 % overlap
 * or no convergence is not.
 */
export function icpVerdict(r: IcpRefineResult): { tone: IcpTone; text: string } {
  if (!r.converged || r.inlier_fraction < 0.2 || r.fitness > 0.1) {
    return { tone: 'poor', text: 'Dårlig overlapning — tilføj ikke kanten på dette grundlag.' };
  }
  if (r.inlier_fraction >= 0.5 && r.fitness <= 0.03) {
    return { tone: 'good', text: 'God overlapning — kanten kan tilføjes.' };
  }
  return { tone: 'fair', text: 'Usikker overlapning — giv kanten en lav vægt.' };
}

/**
 * A translational weight (1/σ²) from an ICP result: σ = the RMS fitness
 * (floored at 5 mm so a near-perfect fit does not dominate the graph), scaled
 * by the overlap and quartered when ICP did not converge. Two significant
 * digits — it is a suggestion the user can overwrite, not a measurement.
 */
export function suggestWeight(r: IcpRefineResult): number {
  const sigma = Math.max(r.fitness, 0.005);
  let w = Math.max(0, Math.min(1, r.inlier_fraction)) / (sigma * sigma);
  if (!r.converged) w *= 0.25;
  w = Math.min(1e5, Math.max(0.01, w));
  return Number(w.toPrecision(2));
}

/** A weight for the form field, with a Danish decimal comma. */
export function formatWeight(w: number): string {
  return String(w).replace('.', ',');
}

// ----------------------------------------------------------------- errors --

export type PoseGraphAction = 'add' | 'reject' | 'icp' | 'optimize' | 'load';

/** What a failed pose-graph call means, in Danish. */
export function poseGraphErrorCopy(err: unknown, action: PoseGraphAction): string {
  if (err instanceof ApiRequestError) {
    if (err.status === 409) {
      return action === 'add'
        ? 'Kanten blev ikke tilføjet: et pipeline-trin kører, eller projektet er aldrig blevet optimeret (kør optimering først).'
        : 'Ændringen blev ikke gemt: et pipeline-trin kører lige nu. Vent til det er færdigt.';
    }
    if (err.status === 404 && action === 'reject') return 'Kanten findes ikke længere — den er allerede afvist.';
    if (err.status === 422 && action === 'icp') return 'Et af billederne mangler dybde eller position, så ICP kan ikke køres.';
    if (err.status === 503 && action === 'icp') return 'Serveren er bygget uden ICP.';
    if (err.status === 503) return 'Projektdatabasen var optaget — prøv igen om et øjeblik.';
    if (err.status === 403) return 'Du har ikke rettigheder til at ændre sagen.';
    return err.message;
  }
  if (err instanceof TypeError) return 'Kunne ikke forbinde til serveren.';
  return err instanceof Error ? err.message : String(err);
}

// ------------------------------------------------------------- optimize ----

/** The newest optimize job the event stream knows, or null. */
export function latestOptimizeJob(jobs: readonly Job[]): Job | null {
  let best: Job | null = null;
  for (const j of jobs) {
    if (j.stage !== 'optimize') continue;
    if (!best || j.submitted_at > best.submitted_at) best = j;
  }
  return best;
}

/** A finished optimize run's identity, or null while none has finished: the graph reloads when it changes. */
export function optimizeDoneKey(job: Job | null): string | null {
  if (!job || job.status === 'queued' || job.status === 'running') return null;
  return `${job.id}:${job.status}`;
}

/** The line under the Kør optimering button. */
export function optimizeStatusText(job: Job | null): string | null {
  if (!job) return null;
  switch (job.status) {
    case 'queued':
      return 'Optimering i kø…';
    case 'running': {
      const f = job.progress?.fraction;
      return typeof f === 'number' ? `Optimerer… ${Math.round(f * 100)} %` : 'Optimerer…';
    }
    case 'succeeded':
      return 'Seneste optimering lykkedes — residualer og positioner er opdateret.';
    case 'failed':
      return `Seneste optimering fejlede${job.error ? `: ${job.error}` : '.'}`;
    case 'cancelled':
      return 'Seneste optimering blev annulleret.';
    default:
      return null;
  }
}

/** The body that brings a rejected edge back. */
export function restoreBody(e: EdgeRef & { weight?: number | null }): PoseGraphEdgeCreate {
  return { from: e.from, to: e.to, type: e.type, weight: e.weight && e.weight > 0 ? e.weight : 1 };
}

/**
 * The world box around an edge's two endpoints, padded by `pad` metres on
 * every side: what the camera frames when an edge is picked from the list.
 * Null when either endpoint has no node or a malformed pose.
 */
export function edgeBox(
  graph: PoseGraph | null | undefined,
  ref: EdgeRef,
  pad = 1.5,
): { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } } | null {
  if (!graph) return null;
  const pos = (id: number) => {
    const n = graph.nodes.find((x) => x.id === id);
    return n ? poseTranslation(n.pose) : null;
  };
  const a = pos(ref.from);
  const b = pos(ref.to);
  if (!a || !b) return null;
  return {
    min: { x: Math.min(a[0], b[0]) - pad, y: Math.min(a[1], b[1]) - pad, z: Math.min(a[2], b[2]) - pad },
    max: { x: Math.max(a[0], b[0]) + pad, y: Math.max(a[1], b[1]) + pad, z: Math.max(a[2], b[2]) + pad },
  };
}
